import { useState } from 'react';
import { ChevronLeft, ChevronRight, Eye } from 'lucide-react';

interface StatsOverlayProps {
  totalSales: number;
  orderCount: number;
  viewerCount: number;
  minimized: boolean;
  onToggle: () => void;
}

const containerStyle: React.CSSProperties = {
  position: 'absolute',
  top: 60,
  left: 14,
  zIndex: 10,
};

const glassBase: React.CSSProperties = {
  background: 'rgba(20,16,30,0.78)',
  backdropFilter: 'blur(12px)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 14,
};

const minimizedStyle: React.CSSProperties = {
  ...glassBase,
  padding: '8px 10px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
};

const expandedStyle: React.CSSProperties = {
  ...glassBase,
  padding: '10px 14px',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
};

const dividerStyle: React.CSSProperties = {
  width: 1,
  height: 22,
  background: 'rgba(255,255,255,0.18)',
  flexShrink: 0,
};

const statColumnStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 2,
};

const statValueStyle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 700,
  lineHeight: 1.2,
};

const statLabelStyle: React.CSSProperties = {
  fontSize: 9,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'rgba(255,255,255,0.6)',
};

const minimizeButtonStyle: React.CSSProperties = {
  width: 22,
  height: 22,
  borderRadius: 6,
  background: 'transparent',
  border: 'none',
  color: 'rgba(255,255,255,0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  padding: 0,
  flexShrink: 0,
};

export function StatsOverlay({
  totalSales,
  orderCount,
  viewerCount,
  minimized,
  onToggle,
}: StatsOverlayProps) {
  const [minimizeBtnHover, setMinimizeBtnHover] = useState(false);

  const formattedSales = `$${totalSales.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  if (minimized) {
    return (
      <div style={containerStyle}>
        <div style={minimizedStyle} onClick={onToggle}>
          <span style={{ color: '#4ade80', fontSize: 12, fontWeight: 600 }}>
            {formattedSales}
          </span>
          <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, fontWeight: 600 }}>
            {' \u00B7 '}
          </span>
          <span
            style={{
              color: 'rgba(255,255,255,0.5)',
              fontSize: 12,
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
            }}
          >
            <Eye size={11} />
            {viewerCount}
          </span>
          <ChevronRight size={12} color="rgba(255,255,255,0.5)" />
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={expandedStyle}>
        <div style={statColumnStyle}>
          <span style={{ ...statValueStyle, color: '#4ade80' }}>{formattedSales}</span>
          <span style={statLabelStyle}>Sales</span>
        </div>
        <div style={dividerStyle} />
        <div style={statColumnStyle}>
          <span style={{ ...statValueStyle, color: '#fff' }}>{orderCount}</span>
          <span style={statLabelStyle}>Orders</span>
        </div>
        <div style={dividerStyle} />
        <div style={statColumnStyle}>
          <span style={{ ...statValueStyle, color: '#fff' }}>{viewerCount}</span>
          <span style={statLabelStyle}>Viewers</span>
        </div>
        <button
          style={{
            ...minimizeButtonStyle,
            color: minimizeBtnHover ? '#fff' : 'rgba(255,255,255,0.7)',
            background: minimizeBtnHover ? 'rgba(255,255,255,0.1)' : 'transparent',
          }}
          onClick={onToggle}
          onMouseEnter={() => setMinimizeBtnHover(true)}
          onMouseLeave={() => setMinimizeBtnHover(false)}
        >
          <ChevronLeft size={14} />
        </button>
      </div>
    </div>
  );
}
